import React, { useContext } from 'react';
import { DatePicker, Segmented, Spin } from 'antd';
import dayjs, { Dayjs } from 'dayjs';

import BuildingsTab from '@totem/components/services/threats/eventsDashboard/report/BuildingsTab';
import { FILTER_KEYS } from '@totem/components/services/threats/eventsDashboard/report/configuration';
import EventsReportContext from '@totem/components/services/threats/eventsDashboard/report/EventsReportContext';
import FindingsTab from '@totem/components/services/threats/eventsDashboard/report/FindingsTab';
import SummaryTab from '@totem/components/services/threats/eventsDashboard/report/SummaryTab';
import {
  DashboardMode,
  ReportTab,
} from '@totem/components/services/threats/eventsDashboard/report/types';
import IBTabs from '@totem/components/ui_components/antDesignOverrides/IBTabs';
import DropDownFilters from '@totem/components/ui_components/reportSettings/dropDownFilters';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';

import './eventsReport.css';

const { RangePicker } = DatePicker;

const lastDays = (days: number): [Dayjs, Dayjs] => [
  dayjs().subtract(days, 'day').startOf('day'),
  dayjs().endOf('day'),
];

const RANGE_PRESETS = [
  { label: 'Last 7 days', value: lastDays(7) },
  { label: 'Last 14 days', value: lastDays(14) },
  { label: 'Last 30 days', value: lastDays(30) },
  { label: 'Last 60 days', value: lastDays(60) },
  { label: 'Last 90 days', value: lastDays(90) },
];

export const EventsReportTitle = () => {
  const { userProfile } = useContext(UserProfileContext);
  const organizationName = userProfile?.organization?.name;

  return (
    <span>
      Events{organizationName ? ` · ${organizationName} Portfolio` : ''}
    </span>
  );
};

export const EventsReportScope = () => {
  const { summary, mode } = useContext(EventsReportContext);

  if (!summary) {
    return null;
  }

  const range =
    summary.rangeStart && !summary.rangeStart.startsWith('0001')
      ? `${dayjs(summary.rangeStart).format('MMM D, YYYY')} – ${dayjs(summary.rangeEnd).format('MMM D, YYYY')}`
      : 'All time';
  return (
    <span styleName="scope">
      {mode === 'resolved' ? 'Resolved issues' : 'Open issues'} · {range}
    </span>
  );
};

const EventsReportLayout = () => {
  const { summary, loading, mode, setMode, activeTab, setActiveTab } =
    useContext(EventsReportContext);
  const { getFilterValues, handleFilter } = useContext(InputContext);

  const dateRange = getFilterValues('dateRange');
  const pickerValue: [Dayjs, Dayjs] | null =
    dateRange.length === 2 ? [dayjs(dateRange[0]), dayjs(dateRange[1])] : null;

  const badge = (count: number | undefined) =>
    count ? <span styleName="tab-badge">{count}</span> : null;

  return (
    <div styleName="report">
      <DropDownFilters showFilterChips keys={FILTER_KEYS} extra={''} />
      <div styleName="title-row">
        <EventsReportScope />
        <div styleName="controls">
          <RangePicker
            value={pickerValue}
            presets={RANGE_PRESETS}
            allowClear
            placeholder={
              mode === 'resolved'
                ? ['Last 30 days', 'today']
                : ['All time', 'today']
            }
            onChange={(values) =>
              handleFilter(
                'dateRange',
                values && values[0] && values[1]
                  ? [
                      values[0].startOf('day').toISOString(),
                      values[1].endOf('day').toISOString(),
                    ]
                  : [],
              )
            }
          />
          <Segmented
            value={mode}
            onChange={(value) => setMode(value as DashboardMode)}
            options={[
              { label: 'Open', value: 'open' },
              { label: 'Resolved', value: 'resolved' },
            ]}
          />
        </div>
      </div>
      {!summary && loading ? (
        <div style={{ padding: 48, textAlign: 'center' }}>
          <Spin />
        </div>
      ) : (
        <Spin spinning={loading}>
          <IBTabs
            activeKey={activeTab}
            onChange={(key) => setActiveTab(key as ReportTab)}
            items={[
              { key: 'summary', label: 'Summary', children: <SummaryTab /> },
              {
                key: 'operations',
                label: (
                  <span>
                    Operations{badge(summary?.groupTotals.operations?.findings)}
                  </span>
                ),
                children: <FindingsTab group="operations" />,
              },
              {
                key: 'security',
                label: (
                  <span>
                    Security{badge(summary?.groupTotals.security?.findings)}
                  </span>
                ),
                children: <FindingsTab group="security" />,
              },
              {
                key: 'buildings',
                label: 'Buildings',
                children: <BuildingsTab />,
              },
            ]}
          />
        </Spin>
      )}
    </div>
  );
};

export default EventsReportLayout;
